import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../data/content";

export const scrollToHash = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(hash);
  };

  return (
    <header
      data-testid="header-navigation"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "border-b border-cafe-line bg-white/90 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a
          href="#inicio"
          data-testid="nav-logo-link"
          onClick={(e) => go(e, "#inicio")}
          aria-label="OESCA Construcciones — Inicio"
        >
          <Logo />
        </a>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Navegación principal">
          {NAV_LINKS.map((l) => (
            <a
              key={l.hash}
              href={l.hash}
              data-testid={`nav-link-${l.hash.slice(1)}`}
              onClick={(e) => go(e, l.hash)}
              className="group relative text-[13px] font-medium tracking-wide text-cafe-ink/80 transition-colors duration-300 hover:text-cafe-dark"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-cafe transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
          <button
            data-testid="nav-cta-cotizar"
            onClick={(e) => go(e, "#contacto")}
            className="group flex items-center gap-2 rounded-full bg-cafe-dark px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-300 hover:bg-cafe-deep"
          >
            Cotizar Proyecto
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-cafe-line text-cafe-dark lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Cerrar menú" : "Abrir menú"}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-cafe-line bg-white/95 backdrop-blur-md lg:hidden"
            aria-label="Navegación móvil"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {[...NAV_LINKS, { label: "Contacto", hash: "#contacto" }].map((l, i) => (
                <motion.a
                  key={l.hash}
                  href={l.hash}
                  data-testid={`nav-mobile-link-${l.hash.slice(1)}`}
                  onClick={(e) => go(e, l.hash)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  className="border-b border-cafe-line/60 py-3 font-display text-2xl text-cafe-dark"
                >
                  {l.label}
                </motion.a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
